import { useState, useEffect } from 'react';
import { Briefcase, Building2, Scale, FileText, CheckCircle, AlertTriangle, Plus, X, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  getFuncionarioPracticeProfile,
  upsertFuncionarioPracticeProfile,
  DEFAULT_PRACTICE_PROFILE,
  FuncionarioPracticeProfileInput,
  splitProfileList
} from '@/services/funcionarioPracticeService';

export function PracticeProfileTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState<FuncionarioPracticeProfileInput>({ ...DEFAULT_PRACTICE_PROFILE });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // States for tag inputs
  const [newArea, setNewArea] = useState('');
  const [newNorm, setNewNorm] = useState('');
  const [newWarning, setNewWarning] = useState('');

  useEffect(() => {
    async function loadProfile() {
      if (!user?.id) return;
      try {
        setIsLoading(true);
        const data = await getFuncionarioPracticeProfile(user.id);
        if (data) {
          setProfile({
            role_title: data.role_title || '',
            entity_type: data.entity_type || 'ayuntamiento',
            practice_areas: data.practice_areas || [],
            priority_norms: data.priority_norms || [],
            response_style: data.response_style || 'practico',
            detail_level: data.detail_level || 'medio',
            mandatory_warnings: data.mandatory_warnings || [],
            notes: data.notes || '',
          });
        }
      } catch (error) {
        console.error('Error loading practice profile:', error);
        toast({
          variant: 'destructive',
          title: 'Error al cargar perfil',
          description: 'No se pudo recuperar tu perfil de práctica.',
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadProfile();
  }, [user?.id, toast]);

  const handleSave = async () => {
    if (!user?.id) return;
    try {
      setIsSaving(true);
      await upsertFuncionarioPracticeProfile(user.id, profile);
      toast({
        title: 'Perfil guardado',
        description: 'Tu perfil de práctica se ha actualizado correctamente y se aplicará al Redactor 2.0.',
      });
    } catch (error) {
      console.error('Error saving practice profile:', error);
      toast({
        variant: 'destructive',
        title: 'Error al guardar',
        description: 'Hubo un problema al guardar los cambios.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Helper functions for dynamic tags
  const addArea = () => {
    if (!newArea.trim()) return;
    const areas = splitProfileList(newArea);
    const updated = Array.from(new Set([...(profile.practice_areas || []), ...areas]));
    setProfile(prev => ({ ...prev, practice_areas: updated }));
    setNewArea('');
  };

  const removeArea = (indexToRemove: number) => {
    setProfile(prev => ({
      ...prev,
      practice_areas: (prev.practice_areas || []).filter((_, i) => i !== indexToRemove)
    }));
  };

  const addNorm = () => {
    if (!newNorm.trim()) return;
    const norms = splitProfileList(newNorm);
    const updated = Array.from(new Set([...(profile.priority_norms || []), ...norms]));
    setProfile(prev => ({ ...prev, priority_norms: updated }));
    setNewNorm('');
  };

  const removeNorm = (indexToRemove: number) => {
    setProfile(prev => ({
      ...prev,
      priority_norms: (prev.priority_norms || []).filter((_, i) => i !== indexToRemove)
    }));
  };

  const addWarning = () => {
    if (!newWarning.trim()) return;
    const warnings = newWarning.split(/[;]/).map(w => w.trim()).filter(Boolean);
    const updated = Array.from(new Set([...(profile.mandatory_warnings || []), ...warnings]));
    setProfile(prev => ({ ...prev, mandatory_warnings: updated }));
    setNewWarning('');
  };

  const removeWarning = (indexToRemove: number) => {
    setProfile(prev => ({
      ...prev,
      mandatory_warnings: (prev.mandatory_warnings || []).filter((_, i) => i !== indexToRemove)
    }));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
        <span className="text-sm text-muted-foreground">Cargando tu perfil de práctica...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Perfil de Práctica Profesional
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Personaliza tu perfil administrativo para que el Redactor 2.0 y la IA adapten los informes, resoluciones y decretos a tu estilo, entidad y puesto.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Cargo y Entidad - Bento Block */}
        <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-4 shadow-sm">
          <div className="flex items-center gap-2 text-amber-500 font-medium mb-1">
            <Briefcase className="h-4 w-4" />
            <span>Puesto y Entidad</span>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role_title" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Cargo o Puesto
            </Label>
            <Input
              id="role_title"
              value={profile.role_title || ''}
              onChange={e => setProfile(prev => ({ ...prev, role_title: e.target.value }))}
              placeholder="Ej: Técnico de Administración General, Secretario-Interventor"
              className="bg-background/50 border-amber-500/10 focus-visible:border-amber-500/30"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="entity_type" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Tipo de Entidad
            </Label>
            <Select
              value={profile.entity_type}
              onValueChange={value => setProfile(prev => ({ ...prev, entity_type: value }))}
            >
              <SelectTrigger id="entity_type" className="bg-background/50 border-amber-500/10">
                <SelectValue placeholder="Selecciona el tipo de entidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ayuntamiento">Ayuntamiento / Municipio</SelectItem>
                <SelectItem value="diputacion">Diputación Provincial</SelectItem>
                <SelectItem value="cabildo">Cabildo Insular</SelectItem>
                <SelectItem value="mancomunidad">Mancomunidad de Municipios</SelectItem>
                <SelectItem value="consell">Consell Insular</SelectItem>
                <SelectItem value="comunidad_autonoma">Comunidad Autónoma</SelectItem>
                <SelectItem value="otros">Otras Entidades Locales / Organismos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Estilo y Detalle - Bento Block */}
        <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-5 shadow-sm">
          <div className="flex items-center gap-2 text-amber-500 font-medium mb-1">
            <FileText className="h-4 w-4" />
            <span>Estilo de Redacción</span>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Estilo de Respuesta
            </Label>
            <RadioGroup
              value={profile.response_style}
              onValueChange={value => setProfile(prev => ({ ...prev, response_style: value }))}
              className="grid grid-cols-2 gap-3"
            >
              <div className="flex items-center space-x-2 p-3 rounded-lg border border-border bg-background/40 hover:bg-muted/50 transition-colors cursor-pointer">
                <RadioGroupItem value="practico" id="style-practico" />
                <Label htmlFor="style-practico" className="flex flex-col cursor-pointer flex-1">
                  <span className="text-xs font-bold">Práctico</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">Enfocado en la realidad de gestión diaria</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2 p-3 rounded-lg border border-border bg-background/40 hover:bg-muted/50 transition-colors cursor-pointer">
                <RadioGroupItem value="formal" id="style-formal" />
                <Label htmlFor="style-formal" className="flex flex-col cursor-pointer flex-1">
                  <span className="text-xs font-bold">Formal</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">Académico, dogmático y jurisprudencial</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Nivel de Detalle
            </Label>
            <RadioGroup
              value={profile.detail_level}
              onValueChange={value => setProfile(prev => ({ ...prev, detail_level: value }))}
              className="grid grid-cols-3 gap-2"
            >
              {['resumido', 'medio', 'exhaustivo'].map(level => (
                <div key={level} className="flex items-center space-x-1.5 p-2 rounded-lg border border-border bg-background/40 hover:bg-muted/50 transition-colors cursor-pointer">
                  <RadioGroupItem value={level} id={`detail-${level}`} />
                  <Label htmlFor={`detail-${level}`} className="cursor-pointer capitalize text-xs font-medium flex-1">
                    {level}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>

      {/* Áreas de Práctica - Bento Block Full */}
      <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-4 shadow-sm">
        <div className="flex items-center gap-2 text-amber-500 font-medium">
          <Building2 className="h-4 w-4" />
          <span>Áreas de Práctica Habituales</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Define tus áreas de competencia más comunes (ej: Contratación Pública, Urbanismo, Personal, Presupuestos) para que el Redactor priorice modelos coherentes.
        </p>

        <div className="flex gap-2">
          <Input
            value={newArea}
            onChange={e => setNewArea(e.target.value)}
            placeholder="Añadir área (ej: Contratación Pública) y pulsa Intro o el botón +"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addArea();
              }
            }}
            className="bg-background/50 border-amber-500/10"
          />
          <Button onClick={addArea} variant="outline" size="icon" className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-500">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {profile.practice_areas && profile.practice_areas.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.practice_areas.map((area, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 animate-fade-in"
              >
                {area}
                <button
                  type="button"
                  onClick={() => removeArea(idx)}
                  className="hover:text-amber-300 rounded-full focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No se han añadido áreas de práctica habituales todavía.</p>
        )}
      </div>

      {/* Normativa Prioritaria - Bento Block Full */}
      <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-4 shadow-sm">
        <div className="flex items-center gap-2 text-amber-500 font-medium">
          <Scale className="h-4 w-4" />
          <span>Normativa y Leyes Prioritarias</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Indica qué normas, leyes o reglamentos autonómicos y locales utilizas con mayor frecuencia. Se integrarán automáticamente en el bloque jurídico del Redactor.
        </p>

        <div className="flex gap-2">
          <Input
            value={newNorm}
            onChange={e => setNewNorm(e.target.value)}
            placeholder="Añadir norma (ej: Ley 9/2017 LCSP, Ley 7/1985 LRBRL) y pulsa +"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNorm();
              }
            }}
            className="bg-background/50 border-amber-500/10"
          />
          <Button onClick={addNorm} variant="outline" size="icon" className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-500">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {profile.priority_norms && profile.priority_norms.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.priority_norms.map((norm, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 animate-fade-in"
              >
                {norm}
                <button
                  type="button"
                  onClick={() => removeNorm(idx)}
                  className="hover:text-amber-300 rounded-full focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No se ha añadido normativa prioritaria todavía.</p>
        )}
      </div>

      {/* Advertencias Obligatorias - Bento Block Full */}
      <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-4 shadow-sm">
        <div className="flex items-center gap-2 text-amber-500 font-medium">
          <AlertTriangle className="h-4 w-4" />
          <span>Advertencias y Cláusulas Obligatorias</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Establece avisos imperativos que deban figurar siempre en tus plantillas (ej: "Aviso LOPD", "Ley 39/2015 art 47 sobre Nulidad de pleno derecho", "Obligación de notificar en plazo").
        </p>

        <div className="flex gap-2">
          <Input
            value={newWarning}
            onChange={e => setNewWarning(e.target.value)}
            placeholder="Añadir cláusula/advertencia (usa ';' para separar varias) y pulsa +"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addWarning();
              }
            }}
            className="bg-background/50 border-amber-500/10"
          />
          <Button onClick={addWarning} variant="outline" size="icon" className="border-amber-500/20 hover:bg-amber-500/10 hover:text-amber-500">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {profile.mandatory_warnings && profile.mandatory_warnings.length > 0 ? (
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.mandatory_warnings.map((warning, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 animate-fade-in"
              >
                {warning}
                <button
                  type="button"
                  onClick={() => removeWarning(idx)}
                  className="hover:text-red-300 rounded-full focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No se han añadido advertencias obligatorias todavía.</p>
        )}
      </div>

      {/* Notas Internas - Bento Block Full */}
      <div className="p-5 rounded-xl border border-amber-500/10 bg-background/30 backdrop-blur-md space-y-3 shadow-sm">
        <div className="flex items-center gap-2 text-amber-500 font-medium">
          <FileText className="h-4 w-4" />
          <span>Notas y Directrices Adicionales</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Añade directrices específicas o pautas internas propias de tu departamento, municipio o forma de tramitar. Serán inyectadas en las fases de recopilación y redacción.
        </p>

        <Textarea
          value={profile.notes || ''}
          onChange={e => setProfile(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Escribe aquí cualquier indicación específica sobre cómo redactar tus decretos, plazos favoritos de firma, plantillas preferidas o advertencias específicas..."
          className="bg-background/50 border-amber-500/10 focus-visible:border-amber-500/30 min-h-[100px]"
        />
      </div>

      {/* Botón de Guardar */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-medium px-6 py-2 rounded-full shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Guardar Perfil de Práctica
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
